<template>
    <div class="stacked-bar-chart">
        <div id="popup" style="display: none;" v-tippy-html>
            <div v-if="hoveredNode" class="popover-content">
                <h3>{{ labels ? labels[hoveredKey] : hoveredKey }}</h3>
                <h4>Presupuesto ejecutado</h4>
                <div class="amount">
                    {{ moneyFormatter(hoveredNode[hoveredKey + '_absolute']) }}
                </div>
                <div class="percentage">
                    {{
                        numeral(hoveredNode[hoveredKey + '_percentage']).format(
                            '0.00%'
                        )
                    }}
                    del ppto del periodo
                </div>
            </div>
        </div>
        <svg
            :width="width"
            :height="height"
            :viewBox="'0 0 ' + width + ' ' + height"
        >
            <g :transform="'translate(' + margin.left + ',' + margin.top + ')'">
                <g class="axis">
                    <text
                        class="y-axis-title"
                        transform="rotate(-90)"
                        text-anchor="middle"
                        :x="-chartHeight / 2"
                        :y="-70"
                    >
                        {{ yAxisTitle }}
                    </text>
                    <text
                        class="x-axis-title"
                        text-anchor="middle"
                        :x="chartWidth / 2"
                        :y="chartHeight + 40"
                    >
                        {{ xAxisTitle }}
                    </text>
                    <g ref="yAxis"></g>
                    <g
                        ref="xAxis"
                        :transform="'translate(0,' + chartHeight + ')'"
                    ></g>
                </g>
                <g v-for="s in stack" :key="s.key">
                    <g
                        v-for="d in s"
                        :key="d.data[dimensions.time[0]]"
                        style="cursor: pointer"
                        @click="detailClicked(d.data, s.key)"
                        @mouseenter="
                            hoveredNode = d.data;
                            hoveredKey = s.key;
                        "
                        v-tippy="{
                            html: '#popup',
                            followCursor: true,
                            arrow: true,
                            animation: 'fade'
                        }"
                    >
                        <!--<title>{{s.key}}: {{numeral(d.data[s.key]).format('0.00%')}}</title>-->
                        <rect
                            :y="y(d[1])"
                            :height="y(d[0]) - y(d[1])"
                            :x="x(d.data[dimensions.time[0]])"
                            :width="x.bandwidth()"
                            :fill="color(s.key)"
                        ></rect>
                    </g>
                </g>
            </g>
        </svg>
    </div>
</template>
<style lang="scss">
.stacked-bar-chart {
    svg {
        width: 100%;
        height: auto;
        .axis {
            .domain {
                stroke-width: 4px;
                stroke: #979797;
            }
            .tick {
                text {
                    font-family: Roboto;
                    font-size: 12px;
                }
                line {
                    stroke-width: 4px;
                    stroke: #979797;
                }
            }
        }
        rect {
            transition: all ease-out 1s;
        }
    }
}
</style>
<script>
import * as d3 from 'd3';
import numeral from 'numeral';
import moneyFormatter from '../../Methods/MoneyFormatter';

export default {
    data: function() {
        return {
            width: 825,
            height: 750,
            margin: {
                left: 80,
                top: 10,
                right: 10,
                bottom: 60
            },
            hoveredNode: null,
            hoveredKey: null
        };
    },
    props: ['data', 'dimensions', 'color', 'xAxisTitle', 'yAxisTitle'],
    props: {
        data: { required: true },
        dimensions: { required: true },
        metric: { default: 'sum' },
        color: { required: true },
        xAxisTitle: { default: null },
        yAxisTitle: { default: null },
        mode: { default: 'absolute' },
        scalePowExponent: { default: 1 },
        labels: { default: null }
    },
    computed: {
        chartWidth: function() {
            return this.width - this.margin.left - this.margin.right;
        },
        chartHeight: function() {
            return this.height - this.margin.top - this.margin.bottom;
        },
        x: function() {
            return d3
                .scaleBand()
                .paddingOuter(0.25)
                .paddingInner(0.5)
                .range([0, this.chartWidth])
                .domain(
                    this.convertedData.map(d => {
                        return d[this.dimensions.time[0]];
                    })
                );
        },
        y: function() {
            return d3
                .scalePow()
                .range([this.chartHeight, 0])
                .domain([
                    0,
                    d3.max(
                        _(this.convertedData)
                            .groupBy(this.dimensions.time[0])
                            .map(d => {
                                return _.sumBy(d, this.currentMetric);
                            })
                            .value(),
                        function(d) {
                            return d;
                        }
                    )
                ])
                .exponent(this.scalePowExponent)
                .nice();
        },
        axisLeft: function() {
            return d3.axisLeft(this.y).tickFormat(d => {
                if (this.mode === 'absolute')
                    return numeral(d / 1000000).format('0,0');
                else if (this.mode === 'percentage')
                    return numeral(d).format('0%');
                else return d;
            });
        },
        axisBottom: function() {
            return d3.axisBottom(this.x);
        },
        currentMetric() {
            if (this.mode === 'absolute') return this.metric;

            if (this.mode === 'percentage') return 'percentage';
        },
        keys: function() {
            return _(this.convertedData)
                .uniqBy(this.dimensions.ordinal[0])
                .map(d => {
                    return d[this.dimensions.ordinal[0]];
                })
                .value();
        },
        convertedData: function() {
            const totals = _(this.data)
                .groupBy(this.dimensions.time[0])
                .mapValues(d => {
                    return _.sumBy(d, this.metric);
                })
                .value();

            return this.data.map(d => {
                const data = _.clone(d);
                data.percentage = d[this.metric] / totals[d.periodo];
                return data;
            });
        },
        stackedData: function() {
            return _(this.convertedData)
                .groupBy(this.dimensions.time[0])
                .map((d, key) => {
                    let result = {};
                    result[this.dimensions.time[0]] = key;
                    d.forEach(d2 => {
                        result[d2[this.dimensions.ordinal[0]] + '_absolute'] =
                            d2[this.metric];
                        result[d2[this.dimensions.ordinal[0]] + '_percentage'] =
                            d2['percentage'];
                        result[d2[this.dimensions.ordinal[0]]] =
                            d2[this.currentMetric];
                    });

                    return result;
                })
                .value();
        },
        stack: function() {
            const stack = d3.stack().keys(this.keys)(this.stackedData);

            return stack;
        }
    },
    methods: {
        numeral: numeral,
        moneyFormatter: moneyFormatter,
        detailClicked: function(d, s) {
            const output = {};
            output[this.dimensions.ordinal[0]] = s;
            output[this.dimensions.time[0]] = d[this.dimensions.time[0]];
            this.$emit('detail-clicked', output);
        }
    },
    watch: {
        data: function() {
            d3.select(this.$refs.yAxis).call(this.axisLeft);
            d3.select(this.$refs.xAxis).call(this.axisBottom);
        }
    }
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/Components/Charts/StackedBar.vue